import type { ReactNode } from "react"
import { useCallback, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { SavedDashboard } from "../services/dashboard-service"

import { BuilderTabs } from "@/components/layouts/builder-tabs"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { ScrollTiltCard } from "@/components/ui/container-scroll-animation"
import { Skeleton } from "@/components/ui/skeleton"
import { SettingsService } from "@/services/settings-service"

import { DashAiPrompt } from "./dash-ai-prompt"
import { DashGreeting } from "./dash-greeting"
import { DashboardTileGrid } from "./dashboard-tile"
import {
  useDashboards,
  useDeleteDashboard,
  useToggleDashboardFavorite,
} from "../hooks/use-dashboards"
import { getRecentlyViewedIds, trackRecentlyViewed } from "../utils/dashboard-helpers"

type DashboardLandingProps = {
  onSendQuestion: (question: string) => void
  disableSend?: boolean
  onOpenDashboard: (dashboard: SavedDashboard) => void
  onEditDashboard: (dashboard: SavedDashboard) => void
  files?: File[]
  onAddFiles?: (files: File[]) => void
  onRemoveFile?: (file: File) => void
  modelControl?: ReactNode
}

export function DashboardLanding({
  onSendQuestion,
  disableSend = false,
  onOpenDashboard,
  onEditDashboard,
  files,
  onAddFiles,
  onRemoveFile,
  modelControl,
}: DashboardLandingProps) {
  const { t } = useTranslation()
  const { data: dashboards = [], isLoading } = useDashboards()
  const deleteMutation = useDeleteDashboard()
  const toggleFavorite = useToggleDashboardFavorite()
  const [deleteTarget, setDeleteTarget] = useState<SavedDashboard | null>(null)
  const scrollContainerRef = useRef<HTMLElement>(null)

  const userName = SettingsService.getInstance().claims.name

  const recentDashboards = useMemo(() => {
    const ids = getRecentlyViewedIds()
    const dashMap = new Map(dashboards.map((d) => [d.id, d]))
    return ids.map((id) => dashMap.get(id)).filter(Boolean) as SavedDashboard[]
  }, [dashboards])

  const favoriteDashboards = useMemo(() => dashboards.filter((d) => d.is_favorite), [dashboards])
  const personalDashboards = useMemo(
    () => dashboards.filter((d) => d.type === "personal"),
    [dashboards]
  )
  const companyDashboards = useMemo(
    () => dashboards.filter((d) => d.type === "company"),
    [dashboards]
  )

  const handleDelete = useCallback(() => {
    if (!deleteTarget) return
    deleteMutation.mutate(
      { dashboardId: deleteTarget.id, type: deleteTarget.type },
      { onSettled: () => setDeleteTarget(null) }
    )
  }, [deleteTarget, deleteMutation])

  const handleOpen = useCallback(
    (dashboard: SavedDashboard) => {
      trackRecentlyViewed(dashboard.id)
      onOpenDashboard(dashboard)
    },
    [onOpenDashboard]
  )

  const handleEdit = useCallback(
    (dashboard: SavedDashboard) => {
      trackRecentlyViewed(dashboard.id)
      onEditDashboard(dashboard)
    },
    [onEditDashboard]
  )

  const handleToggleFavorite = useCallback(
    (dashboard: SavedDashboard) => {
      toggleFavorite.mutate(dashboard.id)
    },
    [toggleFavorite]
  )

  if (isLoading) {
    return (
      <div className="relative flex h-full flex-col overflow-hidden" role="status">
        <div className="from-secondary/25 via-secondary/10 to-secondary/5 pointer-events-none absolute inset-0 bg-gradient-to-tl" />
        <div className="relative flex-1 overflow-y-auto">
          <div className="flex flex-col items-center px-6 pt-40 pb-8">
            <Skeleton className="mb-4 h-8 w-80" />
            <Skeleton className="mb-8 h-4 w-64" />
            <Skeleton className="h-12 w-full max-w-xl rounded-xl" />
          </div>
          <div className="mx-auto max-w-5xl px-6 pb-32">
            <div className="rounded-xl border p-6">
              <div className="mb-6 flex gap-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-8 w-24 rounded-md" />
                ))}
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="flex flex-col gap-2 rounded-lg border p-4">
                    <Skeleton className="h-5 w-20" />
                    <Skeleton className="mt-2 h-5 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                    <div className="mt-auto border-t pt-2">
                      <Skeleton className="h-3 w-32" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <span className="sr-only">{t("common.loading")}</span>
      </div>
    )
  }

  return (
    <div className="relative flex h-full flex-col overflow-hidden">
      {/* Background gradients */}
      <div className="from-secondary/25 via-secondary/10 to-secondary/5 pointer-events-none absolute inset-0 bg-gradient-to-tl" />
      <div className="from-secondary/20 via-secondary/10 pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_80%_80%,_var(--tw-gradient-stops))] to-transparent" />

      {/* Content */}
      <main ref={scrollContainerRef} className="relative flex-1 overflow-y-auto">
        {/* Hero section */}
        <div className="relative flex min-h-[320px] flex-col">
          <div className="relative flex flex-1 flex-col items-center justify-center px-6 pt-40 pb-8">
            <DashGreeting userName={userName} />
            <DashAiPrompt
              disableSend={disableSend}
              files={files}
              modelControl={modelControl}
              onAddFiles={onAddFiles}
              onRemoveFile={onRemoveFile}
              onSend={onSendQuestion}
            />
          </div>
        </div>

        {/* Tabs section with 3D tilt effect */}
        <div className="relative px-6 pb-32">
          <ScrollTiltCard
            containerClassName="mx-auto max-w-5xl"
            scrollContainerRef={scrollContainerRef}
          >
            <div className="bg-card/80 rounded-xl border p-6 shadow-sm backdrop-blur-sm">
              <BuilderTabs
                defaultValue="personal"
                tabs={[
                  {
                    value: "recent",
                    label: t("dash.landing.tabs.recent"),
                    content: (
                      <DashboardTileGrid
                        emptyMessage={t("dash.landing.tabs.recent_empty")}
                        items={recentDashboards}
                        onDelete={setDeleteTarget}
                        onEdit={handleEdit}
                        onOpen={handleOpen}
                        onToggleFavorite={handleToggleFavorite}
                      />
                    ),
                  },
                  {
                    value: "favorites",
                    label: t("dash.landing.tabs.favorites_tab"),
                    content: (
                      <DashboardTileGrid
                        emptyMessage={t("dash.landing.favorites_empty")}
                        items={favoriteDashboards}
                        onDelete={setDeleteTarget}
                        onEdit={handleEdit}
                        onOpen={handleOpen}
                        onToggleFavorite={handleToggleFavorite}
                      />
                    ),
                  },
                  {
                    value: "personal",
                    label: t("dash.landing.tabs.my_dashboards"),
                    content: (
                      <DashboardTileGrid
                        items={personalDashboards}
                        onDelete={setDeleteTarget}
                        onEdit={handleEdit}
                        onOpen={handleOpen}
                        onToggleFavorite={handleToggleFavorite}
                      />
                    ),
                  },
                  {
                    value: "company",
                    label: t("dash.landing.tabs.company_dashboards"),
                    content: (
                      <DashboardTileGrid
                        items={companyDashboards}
                        onDelete={setDeleteTarget}
                        onEdit={handleEdit}
                        onOpen={handleOpen}
                        onToggleFavorite={handleToggleFavorite}
                      />
                    ),
                  },
                ]}
              />
            </div>
          </ScrollTiltCard>
        </div>
      </main>

      {/* Bottom fade overlay */}
      <div className="from-background/80 pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t to-transparent" />

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("dash.landing.delete")}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.title
                ? t("dash.landing.confirm_delete_named", { name: deleteTarget.title })
                : t("dash.landing.confirm_delete")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              {t("dash.landing.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
